import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';

export interface ApiClientConfig {
  baseURL: string;
  getToken: () => string | null;
  setToken?: (token: string) => void;
  clearToken?: () => void;
  onUnauthorized?: () => void;
}

let apiInstance: AxiosInstance | null = null;

export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const api = axios.create({
    baseURL: config.baseURL,
    withCredentials: true,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  api.interceptors.request.use(
    (requestConfig: InternalAxiosRequestConfig) => {
      const token = config.getToken();
      if (token) {
        requestConfig.headers.Authorization = `Bearer ${token}`;
      }
      return requestConfig;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      if (!originalRequest) return Promise.reject(error);

      const url = originalRequest.url || '';
      const isAuthEndpoint = url.startsWith('/auth/');

      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;

        try {
          const refreshResponse = await axios.post(
            `${config.baseURL}/auth/refresh`,
            {},
            { withCredentials: true }
          );
          const { accessToken } = refreshResponse.data;
          if (accessToken) {
            config.setToken?.(accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        } catch {
          config.clearToken?.();
          config.onUnauthorized?.();
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }
  );

  apiInstance = api;
  return api;
}

export function getApiClient(): AxiosInstance | null {
  return apiInstance;
}

export type { AxiosInstance, AxiosError };
